import { FC, Fragment, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { IoClose, IoOpenOutline } from "react-icons/io5"
import { Dialog, Transition } from "@headlessui/react"
import { DocLinkComponet } from "../types"
import { SSpinner } from "@superwall-me/sui"
import useAnalytics, { TGroupOptionalTAnalyticsEvent } from "app/analytics/hooks/useAnalytics"

type CloseButtonProps = {
  onClick: () => void
} & React.HTMLAttributes<HTMLDivElement>

const CloseButtonIcon = () => {
  return (
    <>
      <span className="sr-only">Close panel</span>
      <IoClose className="h-6 w-6" aria-hidden="true" />
    </>
  )
}

const OpenInTabButtonIcon = () => {
  return (
    <>
      <span className="sr-only">Open in tab</span>
      <IoOpenOutline className="h-6 w-6" aria-hidden="true" />
    </>
  )
}

const CloseButton: FC<CloseButtonProps> = forwardRef<HTMLButtonElement, CloseButtonProps>(
  ({ onClick, className, children, ...props }, ref) => {
    return (
      <div className={`absolute  translate-x-full  p-3 flex ${className} `} {...props}>
        <button
          type="button"
          className="rounded-md text-gray-300 hover:text-gray-600 bg-white p-1"
          onClick={onClick}
          ref={ref}
        >
          {children}
        </button>
      </div>
    )
  }
)

CloseButton.displayName = "CloseButton"

const DocOverlay: FC<{
  doc?: DocLinkComponet
  side?: "left" | "right"
  isOpen?: boolean
  didClose?: () => void
  onOpenInTab?: () => void
}> = ({
  onOpenInTab = () => {},
  didClose: _didClose = () => {},
  isOpen,
  doc,
  side: _side = "right",
}) => {
  const [isLoading, setIsLoading] = useState<"loading" | "loaded" | "ready">("loading")
  const [sideState, setSideState] = useState(_side)

  const [isClosing, setIsClosing] = useState(false)

  const side = useMemo(() => {
    if (isClosing) {
      return sideState
    }
    return _side
  }, [isClosing, sideState, _side])

  useEffect(() => {
    if (!isClosing) {
      setSideState(_side)
    }
  }, [_side, isClosing])

  const didClose = useCallback(() => {
    setIsClosing(true)
    setTimeout(() => {
      _didClose()
      setTimeout(() => {
        setIsClosing(false)
      }, 200)
    }, 300)
  }, [_didClose])

  const showInput = isClosing ? false : isOpen || false

  const track = useAnalytics()

  return (
    <>
      <Transition.Root show={showInput} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 overflow-hidden pointer-events-none"
          onClose={() => {}}
        >
          <div className="absolute inset-0 overflow-hidden">
            {/* <Transition.Child
              as={Fragment}
              enter="ease-in-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in-out duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="absolute inset-0 bg-grayscale-darkest bg-opacity-0 transition-opacity" />
            </Transition.Child> */}
            <div
              className={`fixed inset-y-0 ${
                side === "left" ? "left-0 pr-10" : "right-0 pl-10"
              }  max-w-full flex`}
            >
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom={side === "left" ? "-translate-x-full" : "translate-x-full"}
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo={side === "left" ? "-translate-x-full" : "translate-x-full"}
              >
                <div
                  className={` pointer-events-auto relative w-screen max-w-md xl:max-w-lg ${
                    side === "left" ? "mr-auto" : "ml-auto"
                  }`}
                >
                  <div className={`absolute top-0 ${side === "right" ? "left-0" : "right-0"} p-4`}>
                    <Transition.Child
                      as={Fragment}
                      enter="ease-in-out duration-300"
                      enterFrom="opacity-0 scale-0"
                      enterTo="opacity-100 scale-100"
                      leave="ease-in-out duration-300"
                      leaveFrom="opacity-100 scale-100"
                      leaveTo="opacity-0 scale-0"
                    >
                      <div>
                        <CloseButton
                          onClick={didClose}
                          className={`top-0  ${side === "right" ? "-ml-32" : "-ml-8"}`}
                        >
                          <CloseButtonIcon />
                        </CloseButton>
                        <CloseButton
                          onClick={() => {
                            track({
                              name: "Documentation_Click",
                              properties: {
                                url: doc?.link!,
                                view: "new-tab",
                              },
                            } as TGroupOptionalTAnalyticsEvent)
                            onOpenInTab()
                          }}
                          className={`top-12 ${side === "right" ? "-ml-32" : "-ml-8"} mt-30`}
                        >
                          <OpenInTabButtonIcon />
                        </CloseButton>
                      </div>
                    </Transition.Child>
                  </div>
                  <div className="h-full flex flex-col bg-grayscale-darker border-r border-1 border-grayscale-lightest border-opacity-7 shadow-xl overflow-y-scroll">
                    {doc && (
                      <iframe
                        className={`relative w-full h-full transition-opacity duration-100 ease-in-out  ${
                          isLoading === "loading" ? "opacity-0" : "opacity-100"
                        }`}
                        src={doc.link}
                        onLoad={() => {
                          setTimeout(() => {
                            setIsLoading("loaded")
                            setTimeout(() => {
                              setIsLoading("ready")
                            }, 100)
                          }, 200)
                        }}
                      />
                    )}
                    {isLoading !== "ready" && (
                      <div
                        className={`absolute  transition-opacity duration-100 ease-in-out flex justify-center items-center w-full h-full ${
                          isLoading == "loading" ? "opacity-100" : "opacity-0"
                        }`}
                      >
                        <SSpinner />
                      </div>
                    )}
                  </div>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}

export default DocOverlay
